import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Loader2 } from "lucide-react";
import { api, CONDITIONS } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { MachineCard } from "@/components/MachineCard";
import { EnquiryModal } from "@/components/EnquiryModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const SORTS = [
  { value: "latest", label: "Latest" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
  { value: "featured", label: "Featured" },
];

export default function UsedMachines({ listingType = "used", title = "Used Machines", noun = "machines", categoryType = "machine" }) {
  const [params, setParams] = useSearchParams();
  const [machines, setMachines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterOpts, setFilterOpts] = useState({ brands: [], states: [], cities: [] });
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [enquireMachine, setEnquireMachine] = useState(null);

  const page = parseInt(params.get("page") || "1");
  const get = (k) => params.get(k) || "";

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "all") next.set(k, v); else next.delete(k);
    if (k !== "page") next.delete("page");
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());

  useEffect(() => {
    api.get("/categories", { params: { type: categoryType } }).then((r) => setCategories(r.data)).catch(() => {});
    api.get("/machines/filters", { params: { listing_type: listingType } }).then((r) => setFilterOpts(r.data)).catch(() => {});
  }, [listingType, categoryType]);

  useEffect(() => {
    setLoading(true);
    const p = {
      search: get("search"), category_id: get("category"), brand: get("brand"),
      state: get("state"), city: get("city"), condition: get("condition"),
      min_price: get("min_price") || undefined, max_price: get("max_price") || undefined,
      fits: get("fits") || undefined,
      sort: get("sort") || "latest", listing_type: listingType, page, limit: 12,
    };
    api.get("/machines", { params: p })
      .then((r) => { setMachines(r.data.items); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [params, listingType]);

  const onEnquire = useCallback((m) => setEnquireMachine(m), []);

  const activeFilters = ["category", "brand", "state", "city", "condition", "search", "fits"].filter((k) => get(k));

  const FilterPanel = () => (
    <div className="space-y-5">
      {listingType === "spare" && (
        <div>
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Fits Which Machine?</label>
          <Input
            data-testid="filter-compatibility"
            placeholder="e.g. CAT, Komatsu, 320D"
            defaultValue={get("fits")}
            onBlur={(e) => update("fits", e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") update("fits", e.target.value); }}
          />
          <p className="mt-1 text-[11px] text-slate-400">Search by machine brand or model this part fits.</p>
        </div>
      )}
      <FilterSelect label="Category" value={get("category")} onChange={(v) => update("category", v)}
        options={categories.map((c) => ({ value: c.id, label: c.name }))} testid="filter-category" />
      <FilterSelect label="Brand" value={get("brand")} onChange={(v) => update("brand", v)}
        options={filterOpts.brands.map((b) => ({ value: b, label: b }))} testid="filter-brand" />
      <FilterSelect label="State" value={get("state")} onChange={(v) => update("state", v)}
        options={filterOpts.states.map((s) => ({ value: s, label: s }))} testid="filter-state" />
      <FilterSelect label="City" value={get("city")} onChange={(v) => update("city", v)}
        options={filterOpts.cities.map((c) => ({ value: c, label: c }))} testid="filter-city" />
      <FilterSelect label="Condition" value={get("condition")} onChange={(v) => update("condition", v)}
        options={CONDITIONS.map((c) => ({ value: c, label: c }))} testid="filter-condition" />
      <div>
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Price Range</label>
        <div className="flex gap-2">
          <Input data-testid="filter-min-price" placeholder="Min" defaultValue={get("min_price")} onBlur={(e) => update("min_price", e.target.value)} />
          <Input data-testid="filter-max-price" placeholder="Max" defaultValue={get("max_price")} onBlur={(e) => update("max_price", e.target.value)} />
        </div>
      </div>
      {activeFilters.length > 0 && (
        <button data-testid="clear-filters-btn" onClick={clearAll} className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--brand))]">
          <X size={14} /> Clear all filters
        </button>
      )}
    </div>
  );

  return (
    <Layout>
      <Seo
        title={`${title} — Buy & Enquire in India | Mashiniya`}
        description={`Browse ${total} ${noun} on Mashiniya. Filter by category, brand, location, price and condition and connect directly with sellers.`}
        canonical={window.location.origin + (listingType === "new" ? "/new-machines" : listingType === "spare" ? "/spare-parts" : "/used-machines")}
      />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{title}</h1>
          <p className="mt-1 text-slate-500">{total} {noun} available{get("search") && ` for "${get("search")}"`}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:flex lg:px-8">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-4 font-display font-bold text-slate-900">Filters</h3>
            <FilterPanel />
          </div>
        </aside>

        <div className="flex-1">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button data-testid="mobile-filter-btn" onClick={() => setShowFilters(true)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold lg:hidden">
              <SlidersHorizontal size={16} /> Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
            </button>
            <div className="ml-auto w-48">
              <Select value={get("sort") || "latest"} onValueChange={(v) => update("sort", v)}>
                <SelectTrigger data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
          ) : machines.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 py-24 text-center text-slate-500">
              No machines found. Try adjusting your filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {machines.map((m) => <MachineCard key={m.id} m={m} onEnquire={onEnquire} />)}
            </div>
          )}

          {pages > 1 && (
            <div className="mt-10 flex justify-center gap-2">
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  data-testid={`page-${p}`}
                  onClick={() => update("page", String(p))}
                  className={`h-10 w-10 rounded-lg text-sm font-bold ${p === page ? "bg-brand text-white" : "border border-slate-300 text-slate-700"}`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X size={22} /></button>
            </div>
            <FilterPanel />
            <button onClick={() => setShowFilters(false)} className="mt-6 w-full rounded-full bg-brand py-3 font-bold text-white">
              Show {total} Machines
            </button>
          </div>
        </div>
      )}

      <EnquiryModal machine={enquireMachine} open={!!enquireMachine} onClose={() => setEnquireMachine(null)} />
    </Layout>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
      <Select value={value || "all"} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={`All ${label}`} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All {label}</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
